import React from 'react';
import { Popconfirm, Row, Tag, Tooltip } from 'antd';
import { Eye } from 'iconsax-react';
import { PiPencil } from 'react-icons/pi';
import { RiErrorWarningLine } from 'react-icons/ri';
import { Delete } from 'react-iconly';
import { Link } from 'react-router-dom';
import { BsCopy } from 'react-icons/bs';
import isEmpty from 'lodash/isEmpty';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { FiExternalLink } from 'react-icons/fi';
import dayjs from 'dayjs';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import urlPagePushNotificationScheduleDetails from '../../../../../../urls/urlPagePushNotificationScheduleDetails';
import urlPagePushNotificationDetails from '../../../../../../urls/urlPagePushNotificationDetails';
import IntlMessages from '../../../../../../layout/components/lang/IntlMessages';
import { Permissions } from '../../../../../../const/permissions';
import { HasRights, PrivateComponent } from '../../../../../../components/HasRights/HasRights';
import CollapsibleText from '../../../../../../components/CollapsibleText';
import PublishedTag from '../../../../../../components/PublishedTag';
import { DATE_TIME_FORMAT, EMPTY_IMAGE_PLACEHOLDER } from '../../../../../../const/system';
import canInteractWithSchedule from '../../../../../../lib/canInteractWithSchedule';
import getFormattedDate from '../../../../../../lib/getFormattedDate';



export const getPushNotificationsScheduleTableColumns = (
  handleEdit,
  handleDelete,
  handleClone,
  handleImageCardClick,
  tableSize,
) => [
  {
    title: <IntlMessages id="ui-general-id" />,
    dataIndex: 'id',
    align: 'center',
    width: tableSize.width * 0.08,
    sorter: true,
  },
  {
    title: <IntlMessages id="ui-general-preview" />,
    dataIndex: 'preview',
    align: 'center',
    width: tableSize.width * 0.11,
    render: (value, item) => (
      <LazyLoadImage
        onClick={() => {
          if (!isEmpty(item?.preview)) {
            handleImageCardClick(item.preview);
          }
        }}
        style={!isEmpty(item?.preview) ? { cursor: 'pointer' } : {}}
        className='hp-cursor-pointer'
        effect='blur'
        key={item?.id}
        alt="push notifications preview"
        placeholderSrc={EMPTY_IMAGE_PLACEHOLDER}
        src={value ? `${value}?time=${Date.now()}` : EMPTY_IMAGE_PLACEHOLDER}
      />
    ),
  },
  {
    title: <IntlMessages id="ui-general-title" />,
    dataIndex: 'push_notification',
    align: 'center',
    sorter: true,
    width: tableSize.width * 0.2,
    render: (item) => {
      return (
        HasRights([ Permissions.OTHER.NOTIFICATIONS.PUSH.FIND ]) ? (
          <Link
            to={urlPagePushNotificationDetails({ notificationId: item?.data?.id })}
            target="_blank"
            className='hp-d-flex hp-d-flex-justify-center hp-align-items-center'
          >
            <CollapsibleText text={item?.data?.title} maxLength={200} />
            <FiExternalLink size={14} className="hp-ml-4" />
          </Link>
        ) : (
          <CollapsibleText text={item?.data?.title} maxLength={200} />
        )
      );
    },
  },
  {
    title: <IntlMessages id="ui-general-published" />,
    dataIndex: 'is_published',
    align: 'center',
    sorter: true,
    width: tableSize.width * 0.11,
    render: (value, item) => <PublishedTag published={Boolean(value)} publishedDate={item.published_at} />,
  },
  {
    title: <IntlMessages id="ui-general-scheduled-at" />,
    dataIndex: 'scheduled_at',
    align: 'center',
    sorter: true,
    width: tableSize.width * 0.2,
    render: (value, item) => {
      return (
        <div>
          <div>
            {getFormattedDate(value, DATE_TIME_FORMAT)}
            {item?.is_published && dayjs(item.updated_at).isAfter(dayjs(item.published_at)) && (
              <Tooltip placement="right" title={<IntlMessages id="ui-general-unpublished-tooltip-title" />}>
                  &nbsp;
                <ExclamationCircleOutlined className="hp-text-color-warning-1" />
              </Tooltip>
            )}
          </div>
          <div>
            {item.use_utc_zero_time ? (
              <Tag color='default'><IntlMessages id="features-utc" /></Tag>
            ) : ''}
          </div>
        </div>
      );
    },
  },
  {
    title: <IntlMessages id="ui-general-action" />,
    key: 'action',
    align: 'center',
    width: tableSize.width * 0.12,
    render: (item) => {
      return (
        <Row className="da-h-100" align="middle" justify="center">
          <PrivateComponent allowedPermissions={[ Permissions.OTHER.NOTIFICATIONS.PUSH.SCHEDULE.FIND ]}>
            <Tooltip placement="top" title={<IntlMessages id="ui-general-view" />}>
              <div className="hp-text-right">
                <Link
                  to={urlPagePushNotificationScheduleDetails({ scheduleId: item.id })}
                  target="_blank"
                >
                  <Eye
                    size={20}
                    data-testid="view-icon"
                    className="hp-cursor-pointer hp-transition hp-hover-text-color-warning-1 hp-text-color-black-80 hp-m-4"
                  />
                </Link>
              </div>
            </Tooltip>
          </PrivateComponent>
          <PrivateComponent allowedPermissions={[ Permissions.OTHER.NOTIFICATIONS.PUSH.EDIT ]}>
            {canInteractWithSchedule(item) ? (
              <Tooltip placement="top" title={<IntlMessages id="ui-general-edit" />}>
                <div className="hp-text-right">
                  <PiPencil
                    data-testid="edit-icon"
                    onClick={() => handleEdit(item.id)}
                    size={20}
                    className="hp-cursor-pointer hp-transition hp-hover-text-color-warning-1 hp-text-color-black-80 hp-m-4"
                  />
                </div>
              </Tooltip>
            ) : (
              <Tooltip placement="top" title={<IntlMessages id="unable-edit-sended-item-notification" />}>
                <div className="hp-text-right">
                  <PiPencil
                    disabled
                    size={20}
                    data-testid="edit-icon"
                    className="hp-opacity-4 hp-cursor-pointer hp-transition hp-text-color-black-80 hp-m-4"
                  />
                </div>
              </Tooltip>
            )}
          </PrivateComponent>
          <PrivateComponent allowedPermissions={[ Permissions.OTHER.NOTIFICATIONS.PUSH.SCHEDULE.CREATE ]}>
            <Tooltip placement="top" title={<IntlMessages id="ui-general-clone" />}>
              <div className="hp-text-right">
                <BsCopy
                  onClick={() => handleClone(item.id)}
                  size={18}
                  data-testid="clone-icon"
                  className="hp-cursor-pointer hp-transition hp-hover-text-color-success-1 hp-text-color-black-80 hp-m-4"
                />
              </div>
            </Tooltip>
          </PrivateComponent>
          <PrivateComponent allowedPermissions={[ Permissions.OTHER.NOTIFICATIONS.PUSH.SCHEDULE.DELETE ]}>
            {!item.is_published ? (
              <Popconfirm
                title={<IntlMessages id="ui-general-delete-confirm-message" />}
                placement="topLeft"
                onConfirm={() => handleDelete(item.id)}
                okText={<IntlMessages id="ui-general-yes" />}
                cancelText={<IntlMessages id="ui-general-no" />}
                icon={<RiErrorWarningLine className="remix-icon hp-text-color-primary-1" />}
                okButtonProps={{ danger: true }}
              >
                <Tooltip placement="top" title={<IntlMessages id="ui-general-delete" />}>
                  <div className="hp-text-right">
                    <Delete
                      size={20}
                      data-testid="delete-icon"
                      className="hp-cursor-pointer hp-transition hp-hover-text-color-danger-1 hp-text-color-black-80 hp-m-4"
                    />
                  </div>
                </Tooltip>
              </Popconfirm>
            ) : (
              <Tooltip placement="top" title={<IntlMessages id="ui-general-unable-delete-published-item-notification" />}>
                <div className="hp-text-right">
                  <Delete
                    disabled
                    size={20}
                    data-testid="delete-icon"
                    className="hp-opacity-4 hp-cursor-pointer hp-transition hp-text-color-black-80 hp-m-4"
                  />
                </div>
              </Tooltip>
            )}
          </PrivateComponent>
        </Row>
      );
    },
  },
];
